'use client';

import Image from 'next/image';
import { useEffect, useRef, useState, useCallback } from 'react';
import { m } from 'framer-motion';
import styles from './About.module.css';

interface CounterProps {
    end: number;
    suffix: string;
    duration?: number;
}

function AnimatedCounter({ end, suffix, duration = 2000 }: CounterProps) {
    const [count, setCount] = useState(0);
    const ref = useRef<HTMLDivElement>(null);
    const hasAnimated = useRef(false);

    const startCounting = useCallback(() => {
        const startTime = performance.now();
        const animate = (currentTime: number) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
            setCount(Math.floor(eased * end));
            if (progress < 1) {
                requestAnimationFrame(animate);
            }
        };
        requestAnimationFrame(animate);
    }, [end, duration]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !hasAnimated.current) {
                    hasAnimated.current = true;
                    startCounting();
                }
            },
            { threshold: 0.5 }
        );

        const currentRef = ref.current;
        if (currentRef) observer.observe(currentRef);
        return () => { if (currentRef) observer.unobserve(currentRef); };
    }, [startCounting]);

    return <div ref={ref} className={styles.statNumber}>{count.toLocaleString()}{suffix}</div>;
}

const stats = [
    { icon: '🏆', end: 7, suffix: '+', label: 'Tahun Pengalaman' },
    { icon: '🌏', end: 200, suffix: '+', label: 'Kota Terjangkau', accent: true },
    { icon: '📦', end: 50000, suffix: '+', label: 'Pengiriman / Bulan', accent: true },
    { icon: '⭐', end: 98, suffix: '%', label: 'Kepuasan Pelanggan' },
];

export default function About() {
    return (
        <section id="tentang" className={`section ${styles.about}`}>
            <div className="container">
                <div className={styles.content}>
                    <m.div
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, margin: '-100px' }}
                        transition={{ duration: 0.6, ease: "easeOut" }}
                        className={styles.textSide}
                    >
                        <div className={styles.label}>Tentang Kami</div>
                        <h2 className={styles.title}>
                            Perusahaan Ekspedisi & Logistik <br />
                            Lebih dari <span className={styles.titleAccent}>7 Tahun</span> Melayani Indonesia
                        </h2>
                        <p className={styles.description}>
                            Didirikan pada tahun 2019, PT Trans Nusa Pasifik (TNP) kini bertransformasi dengan
                            manajemen baru yang menggabungkan 7 tahun keahlian operasional kargo dengan
                            solusi teknologi digital. Kami hadir untuk menjawab tantangan logistik modern
                            dengan prinsip amanah, efisiensi, dan jangkauan luas ke seluruh pelosok Nusantara.
                        </p>
                        <div className={styles.highlights}>
                            <div className={styles.highlight}>
                                <div className={styles.highlightIcon}>🎯</div>
                                <div className={styles.highlightText}>
                                    <h4>Visi Kami</h4>
                                    <p>Menjadi agregator logistik terpercaya di Indonesia yang menghubungkan setiap
                                        pulau melalui integrasi teknologi dan manajemen yang amanah.</p>
                                </div>
                            </div>
                            <div className={styles.highlight}>
                                <div className={styles.highlightIcon}>💎</div>
                                <div className={styles.highlightText}>
                                    <h4>Misi Kami</h4>
                                    <p>Digitalisasi logistik transparan, menjamin keamanan &amp; ketepatan waktu,
                                        membangun kemitraan amanah tanpa biaya tersembunyi, dan berinovasi
                                        dalam layanan Door-to-Door untuk UMKM maupun Korporat (B2B).</p>
                                </div>
                            </div>
                        </div>
                    </m.div>

                    <m.div
                        initial={{ opacity: 0, x: 50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, margin: '-100px' }}
                        transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
                        className={styles.imageSide}
                    >
                        <div className={styles.imageWrapper}>
                            <Image
                                src="/images/about-logistics.png"
                                alt="Modern Logistics Operation"
                                fill
                                sizes="(max-width: 768px) 100vw, 50vw"
                                className={styles.aboutImage}
                            />
                            <div className={styles.experienceBadge}>
                                <div className={styles.badgeNumber}>7+</div>
                                <div className={styles.badgeText}>Tahun<br />Pengalaman</div>
                            </div>
                        </div>
                    </m.div>
                </div>

                <m.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-100px' }}
                    transition={{ duration: 0.6, ease: "easeOut", delay: 0.4 }}
                    className={styles.statsWrapper}
                >
                    {stats.map((stat) => (
                        <div
                            key={stat.label}
                            className={`${styles.statCard} ${stat.accent ? styles.statCardAccent : ''}`}
                        >
                            <span className={styles.statIcon}>{stat.icon}</span>
                            <AnimatedCounter end={stat.end} suffix={stat.suffix} />
                            <div className={styles.statLabel}>{stat.label}</div>
                        </div>
                    ))}
                </m.div>
            </div>
        </section>
    );
}
